import { useState, useEffect, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import {
  Theme,
  ProfileImage,
  Modal,
  Button,
  ButtonTypes,
  ButtonSize,
  ButtonStyles,
  TextAlign,
  Input,
  MLPVersion,
  RadioButton,
  Status,
  Card,
  FAIcon,
  DropDown,
  PhoneNumber,
  InputValidations,
  InputTypes,
  DatePicker,
  TextArea,
  // ButtonIconPosition,
  // TooltipPreference,
} from '../../../../../ui/components';
import { useAPICall } from '../../../../../hooks';
import { customEventPropType } from '../../../../../ui/components/ImageEditor/types/propsType';
import { DropDownData } from '../../../../../ui/components/Form/DropDown/types/propsType';
import { useAdminToast } from '../../../../../ui/components/AdminToast/AdminToast';
import ChangeButton from '../../../../../assets/image/icons/change_button.svg';
//import TrashButton from '../../../../../assets/image/icons/trash_button.svg';
import AttachmentService from '../../../../../sdk/services/attachmentService';
import RemoveButton from '../../../../../assets/image/icons/remove_button.svg';
import CampaignService from '../../../../../sdk/services/campaignService';
import { countryCodeNumber } from '../../../../../__mockData/phoneNumber';
import { DynamicObjectInterface } from '../../../../../sdk/type';
import ImageEditor from '../../../../../ui/components/ImageEditor/ImageEditor';
import { isValidEmail } from '../../../../../ui/utils/common';
import { InputIconPosition } from '../../../../../ui/components/Form/Input/types/enums';
import {
  LoggerService,
  processBeforeAfterImagesData,
  processBeforeAfterImagesResponse,
  processProfilePictureData,
  formatInbodyDate,
} from '../../../../../ui/utils';
import type { AttachmentImageBA } from '../../../../../sdk/type';
import ProfilePhotoUpload from '../../../../../assets/image/ProfileImageUpload.svg';
import { CampaignConfigDataOrder, OrderData } from '../add-participant/[teamId]';
import ValidationService from '../../../../../sdk/services/validationService';
import InbodyService from '../../../../../sdk/services/inbodyService';
import { ParticipantData, MasterData, SearchParams } from './[partiId]';
import styles from './index.module.scss';

interface DetailsModalProps {
  onSave: DynamicObjectInterface;
  onCancel: DynamicObjectInterface;
  data: ParticipantData | null;
  masterData?: MasterData | null;
  searchParams?: SearchParams | null;
  initialProfileImageUrl?: string | undefined;
  initialHealthGoal?: string | undefined;
  measurementImages?: Record<string, AttachmentImageBA>;
}

//type PointCheckRadio = {
//  productPurchase: boolean;
//  alpSubscription: boolean;
//  winningCriteria: boolean;
//};

// type AddOrderField = {
//   products: boolean;
//   alpInvoiceNumber: boolean;
// };

export const DetailsModal = ({
  onSave,
  onCancel,
  data,
  masterData,
  searchParams,
  initialProfileImageUrl,
}: DetailsModalProps) => {
  const { t } = useTranslation();
  const apiCall = useAPICall();
  const [formData, setFormData] = useState<ParticipantData | null>(JSON.parse(JSON.stringify(data)) as ParticipantData);
  const [dropdownValue, setDropdownValue] = useState('');
  const [phoneValue, setPhoneValue] = useState('');
  const [phoneValid, setPhoneValid] = useState(true);
  const [emailValid, setEmailValid] = useState(true);

  const [showProfileUploadModal, setShowModal] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState<string | undefined>(initialProfileImageUrl);
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);

  useEffect(() => {
    if (formData?.phone) {
      const [countryCode, number] = formData.phone.split(' ');
      setDropdownValue(countryCode);
      setPhoneValue(number);
      setPhoneValid(!(number.length > 0 && (number.length < 8 || number.length > 10)));
    }

    if (formData?.email) {
      setEmailValid(isValidEmail(formData.email));
    }
  }, [formData]);

  const handleOnChange = (value: DynamicObjectInterface, key: keyof ParticipantData) => {
    if (key === 'isNewAbo') {
      value = value === 'true';
    }
    setFormData(prev => ({ ...prev!, [key]: value }));
  };

  const isValidateInputs = (): boolean => {
    let isValid = false;

    if (emailValid && phoneValid && formData?.name !== '') {
      isValid = true;
    }
    return isValid;
  };

  const deleteProfile = async () => {
    const payload = {
      loginId: searchParams?.userId,
    };
    const deleteProfileResponse = await apiCall(AttachmentService.removeProfile, payload);

    if (deleteProfileResponse.status === 200) {
      setProfileImageUrl('');
    }
  };

  const handleFileImage = (e: customEventPropType, view: string) => {
    const file = e.files[0];
    setProfileImageFile(file);
    const objectUrl = URL.createObjectURL(file);
    setProfileImageUrl(objectUrl);
    setShowModal(false);
  };

  const handleFileUpload = async () => {
    try {
      if (profileImageFile && searchParams?.userId) {
        const formData = processProfilePictureData(profileImageFile, searchParams.userId);
        await apiCall(() => AttachmentService.postUploadProfile(formData));
      }
    } catch (error) {
      LoggerService.log('Error uploading file', error);
    }
  };

  const handleFile = (e: ChangeEvent<HTMLInputElement>, key: string) => {
    const file = e.target.files?.[0] as File | null;

    setProfileImageFile(file);
    if (file) {
      const objectUrl = URL.createObjectURL(file);
      setProfileImageUrl(objectUrl);
    } else {
      setProfileImageUrl('');
    }
    setShowModal(false);
  };

  const uploadImageModal = (
    <>
      {showProfileUploadModal && (
        <Modal
          primaryButtonLabel={''}
          headline={'Upload Participant Profile Picture'}
          containerWidth={'550px'}
          containerHeight={'570px'}
          closeIcon={true}
          onClose={() => setShowModal(false)}
        >
          <div>
            {profileImageUrl ? (
              <div className={styles['profile-image-container']}>
                <div className={styles['image-wrapper']}>
                  <img
                    src={profileImageUrl}
                    alt="Before Front View"
                    className={styles['profile-image']}
                    style={{ borderRadius: '50%' }}
                  />
                </div>
                <div className={styles['buttons-container']}>
                  <button
                    className={styles['change-photo']}
                    onClick={() => document.getElementById('profileImageUrl')?.click()}
                  >
                    <img src={ChangeButton} alt="bg" />
                    <input
                      type="file"
                      id="profileImageUrl"
                      accept="image/*"
                      onChange={e => handleFile(e, 'profileImageUrl')}
                      style={{ display: 'none' }}
                    />
                  </button>
                  <button className={styles['remove-photo']} onClick={() => deleteProfile()}>
                    <img src={RemoveButton} alt="bg" />
                  </button>
                </div>
              </div>
            ) : (
              <ImageEditor onFileUpload={e => handleFileImage(e, 'file')} accept="image/*" />
            )}
          </div>
        </Modal>
      )}
    </>
  );

  const showModalFn = () => {
    setShowModal(true);
  };

  return (
    <Modal
      primaryButtonLabel={''}
      headline={`${t('admin.participant.editDetails')}`}
      textAlign={TextAlign.LEFT}
      closeIcon={false}
      containerWidth={'1200px'}
      primaryButton={
        <Button
          title={t('admin.participant.save')}
          type={ButtonTypes.BUTTON}
          onClick={() => {
            if (profileImageUrl) {
              handleFileUpload();
            }
            onSave(formData, initialProfileImageUrl);
          }}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.SOLID}
          data-testid="test-add-button-popup"
          disabled={!isValidateInputs()}
        />
      }
      secondaryButton={
        <Button
          title={t('admin.participant.cancel')}
          type={ButtonTypes.BUTTON}
          onClick={onCancel}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.OUTLINE}
          data-testid="test-cancel-button-popup-delete-modal"
        />
      }
    >
      <div className={styles['modal-details']}>
        <div className={styles['profile-container']}>
          <ProfileImage
            onClick={showModalFn}
            placeholder={data?.name ?? ''}
            src={profileImageUrl ? profileImageUrl : ProfilePhotoUpload}
          />
        </div>
        <div className={styles['modal-details-form']}>
          <Input
            name={'aboId'}
            value={formData?.loginId}
            disabled
            mlpVersion={MLPVersion.ONE}
            label={`${t('admin.participant.aboId')}*`}
          />
          <Input
            name={'participantName'}
            value={formData?.name}
            mlpVersion={MLPVersion.ONE}
            label={`${t('admin.participant.participantName')}*`}
            onChange={e => handleOnChange(e.target.value, 'name')}
            status={formData?.name === '' ? InputValidations.INVALID : InputValidations.DEFAULT}
            message={formData?.name === '' ? t('admin.userSetup.requiredName') : ''}
          />
          <DropDown
            name={'pinLevel'}
            dropdownLabel={`${t('admin.participant.pinLevel')}*`}
            disabledFilter
            mlpVersion={MLPVersion.ONE}
            value={formData?.pinLevel}
            data={masterData?.pin}
            configData={{
              label: 'pinName',
              onSelect: (selectedOption: DropDownData) => {
                handleOnChange(selectedOption.pinName, 'pinLevel');
              },
            }}
          />
          <Input
            name={'uplineId'}
            value={''}
            disabled
            mlpVersion={MLPVersion.ONE}
            label={t('admin.participant.uplineId')}
          />
          <Input
            name={'uplineName'}
            value={''}
            disabled
            mlpVersion={MLPVersion.ONE}
            label={t('admin.participant.uplineName')}
          />
          <Input
            name={'uplinePin'}
            value={''}
            disabled
            mlpVersion={MLPVersion.ONE}
            label={t('admin.participant.uplinePin')}
          />
          <div className={styles['input-container']}>
            <div className={styles['input-label']}>{`${t('admin.participant.phoneNumber')}*`}</div>
            <PhoneNumber
              name={'phoneNumber'}
              data={countryCodeNumber}
              inputValue={phoneValue}
              dropdownValue={dropdownValue}
              placeholder="Phone Number"
              status={!phoneValid ? InputValidations.INVALID : InputValidations.DEFAULT}
              message={!phoneValid ? t('admin.userSetup.requiredPhone') : ''}
              configData={{
                label: 'name',
                onSelect: (e: DropDownData) => {
                  setDropdownValue(e.name as string);
                  if (e.name) {
                    const combinedValue = `${e.name} ${phoneValue}`;
                    setPhoneValue(phoneValue === '' ? '' : phoneValue);
                    handleOnChange(combinedValue, 'phone');
                  } else {
                    setPhoneValue('');
                    handleOnChange('', 'phone');
                  }
                },
              }}
              onInputChange={val => {
                const phoneNumber = val ?? '';
                setPhoneValue(phoneNumber);
                if (phoneNumber) {
                  const combinedValue = `${dropdownValue} ${phoneNumber}`;
                  setPhoneValue(phoneNumber === '' ? '' : phoneNumber);
                  handleOnChange(combinedValue, 'phone');
                } else {
                  setPhoneValue('');
                  handleOnChange('', 'phone');
                }
              }}
            />
          </div>
          <Input
            name={'email'}
            value={formData?.email}
            type={InputTypes.EMAIL}
            mlpVersion={MLPVersion.ONE}
            placeholder="john_doe@domainname.com"
            label={`${t('admin.participant.email')}*`}
            onChange={e => handleOnChange(e.target.value, 'email')}
            status={formData?.email === '' || !emailValid ? InputValidations.INVALID : InputValidations.DEFAULT}
            message={
              formData?.email === ''
                ? t('admin.userSetup.requiredEmail')
                : emailValid
                  ? ''
                  : t('admin.userSetup.invalidEmailFormat')
            }
          />
          <Input
            name={'remarks'}
            value={formData?.remark}
            mlpVersion={MLPVersion.ONE}
            label={t('admin.participant.remarks')}
            onChange={e => handleOnChange(e.target.value, 'remark')}
          />
          <div className={styles['input-radio-btn']}>
            <span className={styles['input-label']}>{t('admin.participant.isNewAbo')}</span>
            <div>
              <RadioButton
                label="True"
                checked={!!formData?.isNewAbo}
                value={'true'}
                onChange={e => handleOnChange(e.target.value, 'isNewAbo')}
              />
              <RadioButton
                label="False"
                checked={!formData?.isNewAbo}
                value={'false'}
                onChange={e => handleOnChange(e.target.value, 'isNewAbo')}
              />
            </div>
          </div>
          <div className={styles['input-radio-btn']}>
            <span className={styles['input-label']}>{t('admin.participant.status')}</span>
            <div>
              <RadioButton
                label="Active"
                checked={formData?.enrollmentStatus === Status.ACTIVE}
                value={Status.ACTIVE}
                onChange={e => handleOnChange(e.target.value, 'enrollmentStatus')}
              />
              <RadioButton
                label="Inactive"
                checked={formData?.enrollmentStatus === Status.INACTIVE}
                value={Status.INACTIVE}
                onChange={e => handleOnChange(e.target.value, 'enrollmentStatus')}
              />
            </div>
          </div>
        </div>
        {uploadImageModal}
      </div>
    </Modal>
  );
};

export const HealthGoalModal = ({ onSave, onCancel, initialHealthGoal }: DetailsModalProps) => {
  const { t } = useTranslation();
  const [HealthGoalData, setHealthGoalData] = useState<string | undefined>(initialHealthGoal);

  const handleTextAreaInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setHealthGoalData(e.target.value);
  };

  return (
    <Modal
      primaryButtonLabel={''}
      headline={''}
      closeIcon={false}
      containerWidth={'1000px'}
      primaryButton={
        <Button
          title={t('admin.participant.save')}
          type={ButtonTypes.BUTTON}
          onClick={() => {
            onSave(HealthGoalData);
          }}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.SOLID}
          data-testid="test-add-button-popup"
        />
      }
      secondaryButton={
        <Button
          title={t('admin.participant.cancel')}
          type={ButtonTypes.BUTTON}
          onClick={onCancel}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.OUTLINE}
          data-testid="test-cancel-button-popup-delete-modal"
        />
      }
    >
      <div className={styles['modal-allocation']}>
        <div className={styles['input-container']}>
          <TextArea
            mlpVersion={MLPVersion.ONE}
            placeholder={t('admin.participant.healthGoalDescription')}
            label={t('admin.participant.healthGoal')}
            resize={false}
            value={HealthGoalData}
            onChange={handleTextAreaInput}
          />
        </div>
      </div>
    </Modal>
  );
};

export const PointAllocationModal = ({ onSave, onCancel, data }: DetailsModalProps) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<ParticipantData | null>(JSON.parse(JSON.stringify(data)) as ParticipantData);
  const apiCall = useAPICall();
  const [orderNumberValid, setOrderNumberValid] = useState(false);
  const [verificationAttempted, setVerificationAttempted] = useState(false);
  const [invalidOrderMessage, setInvalidOrderMessage] = useState<string>('');
  const [campaignData, setCampaignData] = useState<CampaignConfigDataOrder>();

  //const [checkRadio, setCheckRadio] = useState<PointCheckRadio>({
  //  productPurchase: data?.point.purchase ? parseFloat(data.point.purchase ?? '0') > 0 : false,
  //  alpSubscription: data?.point.subscribe ? parseFloat(data.point.subscribe ?? '0') > 0 : false,
  //  winningCriteria: data?.point.winning ? parseFloat(data.point.winning ?? '0') > 0 : false,
  //});
  // const [showAddFields, setShowAddFields] = useState<AddOrderField>({
  //   products: false,
  //   alpInvoiceNumber: false,
  // });
  // const [productFields, setProductFields] = useState({
  //   orderNumber: '',
  //   orderName: '',
  //   invoiceNumber: '',
  // });

  /*const calculateTotal = () => ({
    product: (parseFloat(formData?.point.purchase ?? '0') + parseFloat(formData?.point.purchaseAdj ?? '0')).toFixed(1),
    alp: (parseFloat(formData?.point.subscribe ?? '0') + parseFloat(formData?.point.subscribeAdj ?? '0')).toFixed(1),
    winning: (parseFloat(formData?.point.winning ?? '0') + parseFloat(formData?.point.winningAdj ?? '0')).toFixed(1),
  });*/

  //const [totalPoint, setTotalPoint] = useState(() => calculateTotal());

  useEffect(() => {
    async () => {
      try {
        const campData = await apiCall(CampaignService.getCampaignDetailByCampaignId, data?.campaign?.campaignId ?? '');
        setCampaignData(campData.data.data);
      } catch (error) {
        LoggerService.log('Failed to retrieve campaign Data', error);
      }
    };
  }, []);

  const handleOnChange = (value: DynamicObjectInterface, key: keyof ParticipantData, ...paths: string[]) => {
    const valueStr = value !== null && value !== undefined ? value.toString().trim() : '';
    if (valueStr === '') {
      setFormData(prev => {
        if (!prev) return prev;

        if (key === 'subscribe') {
          if (prev[key].length) {
            if (paths[0] === 'orderNumber') {
              prev[key][0].orderNumber = valueStr;
            }
          }
        } else if( key === 'purchase') {
          if (prev[key].length) {
            if (paths[0] === 'orderNumber') {
              prev[key][0].orderNumber = valueStr;
            } else if (paths[0] === 'orderName') {
              prev[key][0].orderName = valueStr;
            }
          }
        } else {
          const setDeep = (obj: DynamicObjectInterface, i = 0) => {
            if (paths.length !== i) {
              let path = i === 0 ? key : paths[i];
              let _obj = obj[path];
              if (_obj) obj = { ...obj, [path]: setDeep(_obj, i + 1) };
            } else {
              obj[paths[i - 1]] = valueStr;
            }
            return obj;
          };

          if (paths.length > 0) {
            return setDeep(formData) as ParticipantData;
          } else {
            return { ...prev, [key]: valueStr };
          }
        }
        return { ...prev };
      });
      return;
    }

    // Split the input by commas and trim each part
    const orders = valueStr.split(',').map((order: string) => order.trim());

    // Limit to 3 order numbers
    if (orders.length > (campaignData?.activities?.maxOrderNumber ?? 3)) {
      orders.splice(campaignData?.activities?.maxOrderNumber ?? 3); // Keep only the first 3 orders
      value = orders.join(','); // Rebuild the value string
    }

    setFormData(prev => {
      if (!prev) return prev;
      if (key === 'subscribe') {
        if (prev[key].length) {
          if (paths[0] === 'orderNumber') {
            prev[key][0].orderNumber = value;
          } else if (paths[0] === 'orderStatus') {
            prev[key][0].orderStatus = value;
          }
        } else {
          let obj: DynamicObjectInterface = {};
          if (paths[0] === 'orderNumber') {
            obj.orderNumber = value;
          } else if (paths[0] === 'orderStatus') {
            obj.orderStatus = value;
          }
          prev[key].push({
            ...obj,
            orderPoint: 1,
          });
        }
        return { ...prev };
      } else if (key === 'purchase') {
        if (prev[key].length) {
          if (paths[0] === 'orderNumber') {
            const regex = /^[0-9,]*$/;
            if (!regex.test(value)) {
              return { ...prev };
            } else {
              prev[key][0].orderNumber = value;
            }
            prev[key][0].orderStatus = undefined;
          } else if (paths[0] === 'orderName') {
            prev[key][0].orderName = value;
          } else if (paths[0] === 'orderStatus') {
            prev[key][0].orderStatus = value;
          }
        } else {
          let obj: DynamicObjectInterface = {};
          if (paths[0] === 'orderNumber') {
            obj.orderNumber = value;
          } else if (paths[0] === 'orderName') {
            obj.orderName = value;
          } else if (paths[0] === 'orderStatus') {
            obj.orderStatus = value;
          }
          prev[key].push({
            ...obj,
            orderPoint: 1,
          });
        }
        return { ...prev };
      } else {
        const setDeep = (obj: DynamicObjectInterface, i = 0) => {
          if (paths.length !== i) {
            let path = i === 0 ? key : paths[i];
            let _obj = obj[path];
            if (_obj) obj = { ...obj, [path]: setDeep(_obj, i + 1) };
          } else {
            obj[paths[i - 1]] = value;
          }
          return obj;
        };

        if (paths.length > 0) {
          return setDeep(formData) as ParticipantData;
        } else {
          return { ...prev, [key]: value };
        }
      }
    });
  };

  // eslint-disable-next-line
  {
    /*const onPointInputBlur = (value: DynamicObjectInterface, path: keyof PointData) => {
    setFormData(prev => {
      if (!prev) return { ...prev! };
      prev.point[path] = parseFloat(value ?? 0).toFixed(1);
      return { ...prev };
    });
    setTotalPoint(() => calculateTotal());
  };

  const handleRadioBtn = (value: DynamicObjectInterface, key: keyof PointCheckRadio) => {
    setCheckRadio(prev => ({ ...prev, [key]: value === 'true' }));
    setFormData(prev => {
      if (prev && data) {
        switch (key) {
          case 'productPurchase':
            if (value === 'true') {
              prev.point.purchase = data.point.purchase;
              prev.point.purchaseAdj = data.point.purchaseAdj;
            } else {
              prev.point.purchase = '0';
              prev.point.purchaseAdj = '0';
              // setShowAddFields((prev) => ({ ...prev, products: false }));
            }
            break;
          case 'alpSubscription':
            if (value === 'true') {
              prev.point.subscribe = data.point.subscribe;
              prev.point.subscribeAdj = data.point.subscribeAdj;
            } else {
              prev.point.subscribe = '0';
              prev.point.subscribeAdj = '0';
              // setShowAddFields((prev) => ({ ...prev, alpInvoiceNumber: false }));
            }
            break;
          case 'winningCriteria':
            if (value === 'true') {
              prev.point.winning = data.point.winning;
              prev.point.winningAdj = data.point.winningAdj;
            } else {
              prev.point.winning = '0';
              prev.point.winningAdj = '0';
            }
            break;
          default:
            break;
        }
      }
      return { ...prev! };
    });
    setTotalPoint(() => calculateTotal());
  };*/
  }
  const orderList = formData?.purchase.map(orderObj => orderObj.orderNumber).join(',');

  const handleVerifyOrder = async () => {
    setVerificationAttempted(true);

    const payload = {
      campaignId: data?.campaign.campaignId,
      aboId: data?.loginId,
      salesOrderNumber: orderList,
      recId: data?.purchase[0]?.recId,
    };

    if (formData?.purchase[0]?.orderNumber !== '') {
      try {
        const response = await apiCall(ValidationService.validateOrderNumber, payload);
        const result: OrderData = response.data[0];
        if (result.success) {
          setOrderNumberValid(true);
          if (formData){
            formData.purchase[0].orderName = result.orderName;
            formData.purchase[0].orderStatus = 1;
          } 
          setInvalidOrderMessage('The order number(s) provided are valid');
        } else {
          setOrderNumberValid(false);
          setInvalidOrderMessage(
            'The order number(s) provided above will need to be verified by Amway. We will reach out if additional information is needed.',
          );
        }
      } catch (error) {
        LoggerService.log('Error verifying order:', error);
        setOrderNumberValid(false);
        setInvalidOrderMessage('An error occurred while verifying the order numbers. Please try again later.');
      }
    } else {
      setOrderNumberValid(false);
    }
  };

  // const toggleAddField = (field: keyof AddOrderField) => {
  //   setProductFields({
  //     orderNumber: '',
  //     orderName: '',
  //     invoiceNumber: '',
  //   });
  //   setShowAddFields((prev) => {
  //     const updatedFields = Object.keys(prev).reduce((acc, key) => {
  //       acc[key as keyof AddOrderField] = key === field ? !prev[key] : false;
  //       return acc;
  //     }, {} as AddOrderField);
  //     return { ...prev, ...updatedFields };
  //   });
  // };

  // const addOrder = (field: keyof AddOrderField) => {
  //   setFormData((prev) => {
  //     switch (field) {
  //       case 'products':
  //         prev?.purchase.push({
  //           orderNumber: productFields.orderNumber,
  //           orderAmount: 1,
  //           orderName: productFields.orderName,
  //         });
  //         break;
  //       case 'alpInvoiceNumber':
  //         prev?.subscribe.push({
  //           orderNumber: productFields.invoiceNumber,
  //           orderAmount: 1,
  //         });
  //         break;
  //       default:
  //         break;
  //     }
  //     return { ...prev! };
  //   });
  //   setProductFields({
  //     orderName: '',
  //     orderNumber: '',
  //     invoiceNumber: '',
  //   });
  //   toggleAddField(field);
  // };

  // const handleOnDelete = (product: DynamicObjectInterface, field: keyof AddOrderField) => {
  //   setFormData((prev) => {
  //     if (prev) {
  //       switch (field) {
  //         case 'products':
  //           prev.purchase = prev?.purchase.filter((p) => p.orderNumber !== product.orderNumber);
  //           break;
  //         case 'alpInvoiceNumber':
  //           prev.subscribe = prev?.subscribe.filter((p) => p.orderNumber !== product.orderNumber);
  //           break;
  //         default:
  //           break;
  //       }
  //     }
  //     return { ...prev! };
  //   });
  // };

  const iconName: IconName | null =
    formData?.purchase[0]?.orderNumber !== ''
      ? verificationAttempted
        ? orderNumberValid
          ? null
          : null
        : formData?.purchase[0]?.orderStatus === 1
          ? ('circle-check' as IconName)
          : formData?.purchase[0]?.orderStatus === 0
            ? ('xmark-circle' as IconName)
            : null
      : null;

  // Define inputIcon only when iconName is not null
  const inputIconProp = iconName ? { inputIcon: { iconName }, inputIconPosition: InputIconPosition.RIGHT } : {};

  return (
    <Modal
      primaryButtonLabel={''}
      headline={''}
      closeIcon={false}
      containerWidth={'1000px'}
      primaryButton={
        <Button
          title={t('admin.participant.save')}
          type={ButtonTypes.BUTTON}
          onClick={() => {
            onSave(formData);
          }}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.SOLID}
          data-testid="test-add-button-popup"
        />
      }
      secondaryButton={
        <Button
          title={t('admin.participant.cancel')}
          type={ButtonTypes.BUTTON}
          onClick={onCancel}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.OUTLINE}
          data-testid="test-cancel-button-popup-delete-modal"
        />
      }
    >
      <div className={styles['modal-allocation']}>
        <div>
          <div className={styles['slot-header']}>{t('admin.participant.productPurchase')}</div>
          <div className={styles['modal-allocation-form']}>
            <div className={styles['products']}>
              {/* <div className={styles['products-label']}>
                <div className={styles['input-label']}>{t('admin.participant.products')}</div>
                <Button
                  size={ButtonSize.SMALL}
                  icon={{ iconName: 'plus' }}
                  title={t('admin.participant.addProduct')}
                  iconPosition={ButtonIconPosition.RIGHT}
                  buttonStyle={ButtonStyles.OUTLINE}
                  onClick={() => toggleAddField('products')}
                  className={styles['products-add-btn']}
                  disabled={showAddFields.products || !checkRadio.productPurchase}
                />
              </div>
              {formData && formData.purchase.length > 0 && (
                <ul>
                  {formData?.purchase.map((p, i) => {
                    return (
                      <ListItem key={i}>
                        <Chip
                          label={`${p.orderNumber} - ${p.orderName}`}
                          variant={!checkRadio.productPurchase ? 'filled' : 'outlined'}
                          onDelete={!checkRadio.productPurchase ? undefined : () => handleOnDelete(p, 'products')}
                        />
                      </ListItem>
                    );
                  })}
                </ul>
              )}
              {showAddFields.products && (
                <div className={styles['products-add-fields']}>
                  <Input
                    name={'orderNumber'}
                    value={productFields?.orderNumber}
                    mlpVersion={MLPVersion.ONE}
                    label={`${t('admin.participant.orderNumber')}*`}
                    onChange={(e) => setProductFields((p) => ({ ...p, orderNumber: e.target.value }))}
                  />
                  <Input
                    name={'orderName'}
                    value={productFields?.orderName}
                    mlpVersion={MLPVersion.ONE}
                    label={`${t('admin.participant.productName')}*`}
                    onChange={(e) => setProductFields((p) => ({ ...p, orderName: e.target.value }))}
                  />
                  <div className={styles['products-add-fields-btn']}>
                    <button className={styles['cancel']} onClick={() => toggleAddField('products')}>
                      <FaTimes />
                    </button>
                    <button
                      className={styles['check']}
                      disabled={!productFields?.orderNumber || !productFields?.orderName}
                      onClick={() => addOrder('products')}
                    >
                      <FaCheck />
                    </button>
                  </div>
                </div>
              )} */}

              <Input
                name={'orderNumber'}
                value={formData?.purchase[0]?.orderNumber}
                mlpVersion={MLPVersion.ONE}
                label={`${t('admin.participant.orderNumber')}`}
                onChange={e => {
                  handleOnChange(e.target.value || null, 'purchase', 'orderNumber');
                  if (verificationAttempted) setVerificationAttempted(false);
                }}
                message={
                  formData?.purchase[0]?.orderNumber !== ''
                    ? verificationAttempted
                      ? orderNumberValid
                        ? `Valid Order Number(s)`
                        : invalidOrderMessage
                      : `*Enter up to ${campaignData?.activities.maxOrderNumber ?? 3} order numbers, in full and separated by commas (e.g. "50004212345, 50004267890, 50004211223").`
                    : `*Enter up to ${campaignData?.activities.maxOrderNumber ?? 3} order numbers, in full and separated by commas (e.g. "50004212345, 50004267890, 50004211223").`
                }
                status={
                  formData?.purchase[0]?.orderNumber !== ''
                    ? verificationAttempted
                      ? orderNumberValid
                        ? InputValidations.VALID
                        : InputValidations.WARNING
                      : formData?.purchase[0]?.orderStatus === 1
                        ? InputValidations.VALID
                        : formData?.purchase[0]?.orderStatus === 0
                          ? InputValidations.INVALID
                          : InputValidations.DEFAULT
                    : InputValidations.DEFAULT
                }
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    handleVerifyOrder();
                  }
                }}
                {...inputIconProp}
              />
              <Input
                name={'orderName'}
                value={formData?.purchase[0]?.orderName}
                mlpVersion={MLPVersion.ONE}
                label={`${t('admin.participant.productName')}`}
                onChange={e => handleOnChange(e.target.value || null, 'purchase', 'orderName')}
              />
              <DropDown
                name={'orderStatus'}
                dropdownLabel={`${t('admin.participant.orderStatus')}*`}
                disabledFilter
                mlpVersion={MLPVersion.ONE}
                value={
                  formData?.purchase[0]?.orderStatus === null
                    ? ''
                    : formData?.purchase[0]?.orderStatus === 1
                      ? 'YES'
                      : 'NO'
                }
                data={[
                  { value: 1, title: 'YES' },
                  { value: 0, title: 'NO' },
                ]}
                configData={{
                  label: 'title',
                  onSelect: (selectedOption: DropDownData) => {
                    handleOnChange(selectedOption.value, 'purchase', 'orderStatus');
                  },
                }}
              />
            </div>
            <div>
              <br />
              <div className={styles['order-number-container']}>
                <Button
                  className={styles['verify-order']}
                  size={ButtonSize.SMALL}
                  disabled={formData?.purchase[0]?.orderNumber === ''}
                  title="Verify Order"
                  onClick={handleVerifyOrder}
                />
              </div>
            </div>
            {/*<div className={styles['point-input']}>
              <Input
                name="point"
                label={t('admin.participant.point')}
                mlpVersion={MLPVersion.ONE}
                type={InputTypes.NUMBER}
                value={formData?.point.purchase}
                onChange={e => handleOnChange(e.target.value || null, 'point', 'purchase')}
                onBlur={e => onPointInputBlur(e.target.value || null, 'purchase')}
                disabled={!checkRadio.productPurchase}
              />
              <div className={styles['point-input-operation']}>+</div>
              <Input
                name="adjustedPoint"
                label={t('admin.participant.adjustment')}
                mlpVersion={MLPVersion.ONE}
                type={InputTypes.NUMBER}
                value={formData?.point.purchaseAdj}
                onChange={e => handleOnChange(e.target.value || null, 'point', 'purchaseAdj')}
                onBlur={e => onPointInputBlur(e.target.value || null, 'purchaseAdj')}
                disabled={!checkRadio.productPurchase}
              />
              <div className={styles['point-input-operation']}>=</div>
              <Input
                name="totalPoint"
                label={t('admin.participant.total')}
                mlpVersion={MLPVersion.ONE}
                type={InputTypes.NUMBER}
                disabled
                value={totalPoint.product}
              />
            </div>*/}
          </div>
        </div>
        <div>
          <div className={styles['slot-header']}>{t('admin.participant.alpSubsription')}</div>
          <div className={styles['modal-allocation-form']}>
            <div className={styles['products']}>
              {/* <div className={styles['products-label']}>
                <div className={styles['input-label']}>{t('admin.participant.alpInvoiceNumber')}</div>
                <Button
                  size={ButtonSize.SMALL}
                  icon={{ iconName: 'plus' }}
                  title={t('admin.participant.addInvoiceNumber')}
                  iconPosition={ButtonIconPosition.RIGHT}
                  buttonStyle={ButtonStyles.OUTLINE}
                  onClick={() => toggleAddField('alpInvoiceNumber')}
                  className={styles['products-add-btn']}
                  disabled={showAddFields.alpInvoiceNumber || !checkRadio.alpSubscription}
                />
              </div>
              {formData && formData?.subscribe.length > 0 && (
                <ul>
                  {formData?.subscribe.map((s, i) => {
                    return (
                      <ListItem key={i}>
                        <Chip
                          label={`${s.orderNumber}`}
                          variant={!checkRadio.alpSubscription ? 'filled' : 'outlined'}
                          onDelete={
                            !checkRadio.alpSubscription ? undefined : () => handleOnDelete(s, 'alpInvoiceNumber')
                          }
                        />
                      </ListItem>
                    );
                  })}
                </ul>
              )}
              {showAddFields.alpInvoiceNumber && (
                <div className={styles['products-add-fields']}>
                  <Input
                    name={'invoiceNumber'}
                    value={productFields?.invoiceNumber}
                    mlpVersion={MLPVersion.ONE}
                    label={`${t('admin.participant.invoiceNumber')}*`}
                    onChange={(e) => setProductFields((p) => ({ ...p, invoiceNumber: e.target.value }))}
                  />
                  <div className={styles['products-add-fields-btn']}>
                    <button className={styles['cancel']} onClick={() => toggleAddField('alpInvoiceNumber')}>
                      <FaTimes />
                    </button>
                    <button
                      className={styles['check']}
                      disabled={!productFields?.invoiceNumber}
                      onClick={() => addOrder('alpInvoiceNumber')}
                    >
                      <FaCheck />
                    </button>
                  </div>
                </div>
              )} */}
              <Input
                name={'invoiceNumber'}
                value={formData?.subscribe[0]?.orderNumber}
                mlpVersion={MLPVersion.ONE}
                label={`${t('admin.participant.invoiceNumber')}*`}
                onChange={e => handleOnChange(e.target.value || null, 'subscribe', 'orderNumber')}
              />
              <DropDown
                name={'invoiceStatus'}
                dropdownLabel={`${t('admin.participant.orderStatus')}*`}
                disabledFilter
                mlpVersion={MLPVersion.ONE}
                value={
                  formData?.subscribe[0]?.orderStatus === null
                    ? ''
                    : formData?.subscribe[0]?.orderStatus === 1
                      ? 'YES'
                      : 'NO'
                }
                data={[
                  { value: 1, title: 'YES' },
                  { value: 0, title: 'NO' },
                ]}
                configData={{
                  label: 'title',
                  onSelect: (selectedOption: DropDownData) => {
                    handleOnChange(selectedOption.value, 'subscribe', 'orderStatus');
                  },
                }}
              />
            </div>
            {/*<div className={styles['point-input']}>
              <Input
                name="point"
                label={t('admin.participant.point')}
                mlpVersion={MLPVersion.ONE}
                type={InputTypes.NUMBER}
                value={formData?.point.subscribe}
                onChange={e => handleOnChange(e.target.value || null, 'point', 'subscribe')}
                onBlur={e => onPointInputBlur(e.target.value || null, 'subscribe')}
                disabled={!checkRadio.alpSubscription}
              />
              <div className={styles['point-input-operation']}>+</div>
              <Input
                name="adjustedPoint"
                label={t('admin.participant.adjustment')}
                mlpVersion={MLPVersion.ONE}
                type={InputTypes.NUMBER}
                value={formData?.point.subscribeAdj}
                onChange={e => handleOnChange(e.target.value || null, 'point', 'subscribeAdj')}
                onBlur={e => onPointInputBlur(e.target.value || null, 'subscribeAdj')}
                disabled={!checkRadio.alpSubscription}
              />
              <div className={styles['point-input-operation']}>=</div>
              <Input
                name="totalPoint"
                label={t('admin.participant.total')}
                mlpVersion={MLPVersion.ONE}
                type={InputTypes.NUMBER}
                value={totalPoint.alp}
                disabled
              />
            </div>*/}
          </div>
        </div>
        <div>
          {/*<div className={styles['slot-header']}>{t('admin.participant.criteriaAchieved')}</div>
          <div className={styles['modal-allocation-form']}>
            <div className={styles['point-input']}>
              <Input
                name="point"
                mlpVersion={MLPVersion.ONE}
                type={InputTypes.NUMBER}
                label={t('admin.participant.point')}
                value={formData?.point.winning}
                onChange={e => handleOnChange(e.target.value || null, 'point', 'winning')}
                onBlur={e => onPointInputBlur(e.target.value || null, 'winning')}
                disabled={true}
              />
              <div className={styles['point-input-operation']}>+</div>
              <Input
                name="adjustedPoint"
                mlpVersion={MLPVersion.ONE}
                label={t('admin.participant.adjustment')}
                type={InputTypes.NUMBER}
                value={formData?.point.winningAdj}
                onChange={e => handleOnChange(e.target.value || null, 'point', 'winningAdj')}
                onBlur={e => onPointInputBlur(e.target.value || null, 'winningAdj')}
                disabled={false}
              />
              <div className={styles['point-input-operation']}>=</div>
              <Input
                name="totalPoint"
                label={t('admin.participant.total')}
                mlpVersion={MLPVersion.ONE}
                type={InputTypes.NUMBER}
                value={totalPoint.winning}
                disabled
              />
            </div>
            {/* <div className={styles['input-radio-btn']}>
              <RadioButton
                label={t('admin.participant.yes')}
                checked={checkRadio.winningCriteria}
                value={'true'}
                onChange={(e) => handleRadioBtn(e.target.value, 'winningCriteria')}
              />
              <RadioButton
                label={t('admin.participant.no')}
                checked={!checkRadio.winningCriteria}
                value={'false'}
                onChange={(e) => handleRadioBtn(e.target.value, 'winningCriteria')}
              />
            </div> 
          </div>*/}
        </div>
        <div className={styles['modal-allocation-remarks']}>
          <div style={{ width: '50%' }}>
            <Input
              name="remarks"
              label={t('admin.participant.remarks')}
              value={formData?.point.remarks}
              mlpVersion={MLPVersion.ONE}
              onChange={e => handleOnChange(e.target.value, 'point', 'remarks')}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export const RewardGiftModal = ({ onSave, onCancel, data, masterData }: DetailsModalProps) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<ParticipantData | null>(JSON.parse(JSON.stringify(data)) as ParticipantData);
  const [showDelete, setShowDelete] = useState(-1);
  const [invalid, setInvalid] = useState(true);

  const rewardTypeList = [
    { label: '', value: '' },
    {
      label: 'Gift Only',
      value: 'GIFT',
    },
    {
      label: 'Voucher',
      value: 'VOUCHER',
    },
  ];

  useEffect(() => {
    const validateInputs = (formData?.rewards || []).some(
      reward =>
        !reward.rewardCode && reward.rewardStatus !== 'REMOVED' && reward.rewardStatus !== 'REMOVED_BY_ADJUSTMENT',
    );
    setInvalid(validateInputs);
  }, [formData?.rewards]);

  const handleOnChange = (value: DynamicObjectInterface, name: string) => {
    if (name.startsWith('rewards.')) {
      setFormData(prev => {
        if (prev?.rewards && Array.isArray(prev.rewards)) {
          const updatedRewards = prev.rewards.map((reward, index) => {
            if (name.split('.')[1] === reward.rewardRecId.toString()) {
              const propertyName = name.split('.')[2];
              if (propertyName === 'rewardCode') {
                return {
                  ...reward,
                  [propertyName]: value,
                  rewardId: masterData?.reward?.find(i => i.rewardCode === value)?.recId,
                  rewardName: masterData?.reward?.find(i => i.rewardCode === value)?.rewardName,
                };
              } else if (propertyName === 'redeemDate') {
                return {
                  ...reward,
                  [propertyName]: value,
                  status: value ? 'USED' : 'ACTIVE',
                };
              } else {
                return {
                  ...reward,
                  [propertyName]: value,
                };
              }
            }
            return reward;
          });
          return { ...prev, rewards: updatedRewards };
        }
        return prev;
      });
    } else {
      setFormData(prev => ({ ...prev!, [name]: value }));
    }
  };

  const handleAddReward = () => {
    setFormData(prev => {
      if (prev?.rewards && Array.isArray(prev.rewards)) {
        const newReward = {
          rewardStatus: 'ACTIVE',
          isNew: true,
          campaignStatRecId: formData?.campaignStatRecId,
          rewardRecId: `NEW-${new Date()}`,
        };
        return { ...prev, rewards: [...prev.rewards, newReward] };
      }
      return prev;
    });
  };

  const handleDeleteButton = (rewardRecId: string) => {
    const index = formData?.rewards?.findIndex(i => i.rewardRecId === rewardRecId);
    if (index !== null && index !== undefined) setShowDelete(index);
  };

  const handleDeleteReward = (index: number) => {
    setFormData(prev => {
      if (prev?.rewards && Array.isArray(prev.rewards) && index >= 0 && index < prev.rewards.length) {
        const updatedRewards = [...prev.rewards];
        updatedRewards[index] = {
          ...updatedRewards[index],
          rewardStatus: 'REMOVED',
        };

        return { ...prev, rewards: updatedRewards };
      }
      return prev;
    });
    setShowDelete(-1);
  };

  const deletePrompt = (
    <>
      {showDelete !== -1 && (
        <Modal
          primaryButtonLabel={''}
          headline={`${t('admin.rewardMaster.deleteReward')}`}
          closeIcon={false}
          containerWidth={'460px'}
          primaryButton={
            <Button
              title={t('admin.rewardMaster.confirm')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                handleDeleteReward(showDelete);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
              data-testid="test-add-button-popup"
            />
          }
          secondaryButton={
            <Button
              title={t('admin.rewardMaster.cancel')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                setShowDelete(-1);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.OUTLINE}
              data-testid="test-cancel-button-popup-delete-modal"
            />
          }
        />
      )}
    </>
  );
  return (
    <Modal
      primaryButtonLabel={''}
      headline={t('admin.participant.editRewardDetail')}
      textAlign={TextAlign.LEFT}
      closeIcon={false}
      containerWidth={'1200px'}
      primaryButton={
        <Button
          title={t('admin.participant.save')}
          type={ButtonTypes.BUTTON}
          onClick={() => onSave(formData)}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.SOLID}
          data-testid="test-add-button-popup"
          disabled={invalid}
        />
      }
      secondaryButton={
        <Button
          title={t('admin.participant.cancel')}
          type={ButtonTypes.BUTTON}
          onClick={onCancel}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.OUTLINE}
          data-testid="test-cancel-button-popup-delete-modal"
        />
      }
    >
      <div
        className={`${styles['modal-reward']} ${
          formData?.rewards && formData?.rewards?.length > 0 ? styles['increased-height'] : ''
        }`}
      >
        {formData?.rewards
          ?.filter(item => item?.rewardStatus && !['REMOVED', 'REMOVED_BY_ADJUSTMENT'].includes(item?.rewardStatus))
          .map((item, index) => (
            <Card key={item.rewardRecId}>
              <div className={styles['modal-reward-headline']}>
                <div>Reward {index + 1}</div>
                {!item?.rewardRecId?.toString()?.includes('NEW') && (
                  <FAIcon
                    iconName={'trash-can'}
                    onClick={e => {
                      handleDeleteButton(item?.rewardRecId);
                      e.stopPropagation();
                    }}
                    theme={Theme.RED}
                    title={'Delete'}
                    className={styles['modal-reward-delete']}
                  />
                )}
              </div>
              <div className={styles['modal-details-form']}>
                <DropDown
                  name={'rewardCode'}
                  dropdownLabel={`${t('admin.rewardMaster.rewardCode')}*`}
                  disabledFilter
                  mlpVersion={MLPVersion.ONE}
                  value={item?.rewardCode}
                  data={masterData?.reward}
                  configData={{
                    label: 'rewardCode',
                    onSelect: (selectedOption: DropDownData) => {
                      handleOnChange(selectedOption.rewardCode, `rewards.${item.rewardRecId}.rewardCode`);
                    },
                  }}
                />
                <Input
                  name={'rewardName'}
                  value={item.rewardName}
                  disabled={true}
                  mlpVersion={MLPVersion.ONE}
                  label={t('admin.rewardMaster.rewardName')}
                />
                <DropDown
                  name={'rewardType'}
                  dropdownLabel={t('admin.participant.rewardType')}
                  disabledFilter
                  mlpVersion={MLPVersion.ONE}
                  value={rewardTypeList?.find(i => i.value === item?.rewardType)?.label}
                  data={rewardTypeList}
                  configData={{
                    label: 'label',
                    onSelect: (selectedOption: DropDownData) => {
                      handleOnChange(selectedOption.value, `rewards.${item.rewardRecId}.rewardType`);
                    },
                  }}
                />
                <DatePicker
                  data-testid={'test-date-picker-single'}
                  calendarHeading={t('admin.participant.collectDate')}
                  inputContainerHeight="36px"
                  containerWidth="450px"
                  value={item?.redeemDate ? new Date(item?.redeemDate) : undefined}
                  inputLabel={`Collect Date`}
                  onChange={date => {
                    handleOnChange(date, `rewards.${item.rewardRecId}.redeemDate`);
                  }}
                  onClear={() => handleOnChange('', `rewards.${item.rewardRecId}.redeemDate`)}
                  allowRemove={true}
                />
                <Input
                  name={'remarks'}
                  value={item.rewardRemarks}
                  mlpVersion={MLPVersion.ONE}
                  label={t('admin.participant.remarks')}
                  onChange={e => handleOnChange(e.target.value, `rewards.${item.rewardRecId}.rewardRemarks`)}
                />
              </div>
              {deletePrompt}
            </Card>
          ))}
        <Button
          title={t('admin.participant.addReward')}
          type={ButtonTypes.BUTTON}
          onClick={handleAddReward}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.OUTLINE}
          data-testid="test-add-button-popup"
        />
      </div>
    </Modal>
  );
};

export const ResultModal = ({
  onSave,
  onCancel,
  data,
  masterData,
  searchParams,
  measurementImages,
}: DetailsModalProps) => {
  const { t } = useTranslation();
  const apiCall = useAPICall();
  const defaultWeightedDate = data?.weightIn?.weightedDate ? new Date(data.weightIn.weightedDate) : new Date();
  const initialFormData: ParticipantData = {
    ...data!,
    weightIn: {
      acknowledgeBy: '',
      acknowledgeDateTime: undefined,
      bodyFat: 0,
      height: 0,
      machine: '',
      muscleKilogram: 0,
      musclePercentage: 0,
      weight: 0,
      remarks: '',
      weightedDate: defaultWeightedDate.toISOString(),
    },
  };

  // Use a default value for formData to help TypeScript infer the type
  const [formData, setFormData] = useState<ParticipantData | null>(data);
  const [images, setImages] = useState<Record<string, AttachmentImageBA>>({
    before_front: {
      photoId: measurementImages?.before_front?.photoId || 0,
      url: measurementImages?.before_front?.url || undefined,
      file: null,
    },
    after_front: {
      photoId: measurementImages?.after_front?.photoId || 0,
      url: measurementImages?.after_front?.url || undefined,
      file: null,
    },
    before_side: {
      photoId: measurementImages?.before_side?.photoId || 0,
      url: measurementImages?.before_side?.url || undefined,
      file: null,
    },
    after_side: {
      photoId: measurementImages?.after_side?.photoId || 0,
      url: measurementImages?.after_side?.url || undefined,
      file: null,
    },
  });

  type InbodyDateOption = {
    date: string;
    label: string;
  };

  // Check if formData exists and set it if needed
  useEffect(() => {
    if (!formData?.weightIn.weightedDate) {
      setFormData(initialFormData);
    }
  }, [formData, initialFormData]);

  const handleInputChange = (name: string, value: DynamicObjectInterface) => {
    setFormData((prevData: ParticipantData | null) => {
      if (!prevData) {
        // If prevData is null, return an initial object
        return {
          [name]: value,
        } as ParticipantData;
      }

      // Create a copy of the previous data to avoid mutation
      const newData: ParticipantData = { ...prevData };

      // Use a type assertion to handle the dynamic property assignment
      let currentLevel: DynamicObjectInterface = newData;
      const keys = name.split('.');

      for (const key of keys.slice(0, -1)) {
        currentLevel = currentLevel[key];
      }

      currentLevel[keys[keys.length - 1]] = value;
      return { ...newData };
    });
  };

  const commonInputProps = {
    type: InputTypes.NUMBER,
    mlpVersion: MLPVersion.ONE,
  };

  const currentDate = new Date();
  const regex = /^\d*\.?\d{0,2}$/;

  const handleFileImage = (e: customEventPropType, view: string, isBefore: boolean) => {
    const file = e.files[0];
    const imageUrlSetter = isBefore ? 'before' : 'after';
    const imageStateSetter = view === 'front' ? 'front' : 'side';

    // Check if the original filename is "image.jpg"
    const originalFileName = file.name;
    const isOriginalImageName = originalFileName === 'image.jpg';

    // Get current date and time to create a unique filename
    const currentDate = new Date();
    const timestamp = currentDate.getTime(); // Get timestamp in milliseconds
    const extension = file.name.split('.').pop(); // Get the file extension

    let newFileName = originalFileName; // Default to original filename
    if (isOriginalImageName) {
      newFileName = `${timestamp}.${extension}`; // Create new filename with timestamp
    }

    setImages((prevImages: Record<string, AttachmentImageBA>) => ({
      ...prevImages,
      [`${imageUrlSetter}_${imageStateSetter}`]: {
        photoId: prevImages[`${imageUrlSetter}_${imageStateSetter}`].photoId,
        url: URL.createObjectURL(file),
        file: new File([file], newFileName), // Create a new File object with the renamed file if needed
      },
    }));
  };

  const handleRemoveImage = (imageUrlSetter: string, imageStateSetter: string) => {
    setImages((prevImages: Record<string, AttachmentImageBA>) => ({
      ...prevImages,
      [`${imageUrlSetter}_${imageStateSetter}`]: {
        photoId: prevImages[`${imageUrlSetter}_${imageStateSetter}`].photoId,
        url: '',
        file: null,
      },
    }));
  };

  const handleFile = (e: ChangeEvent<HTMLInputElement>, key: string, isBefore: boolean) => {
    const file = e.target.files?.[0] as File | null;
    if (file) {
      const url: string = URL.createObjectURL(file);
      setImages(prevImages => ({
        ...prevImages,
        [key]: { ...prevImages[key], file, url },
      }));
    }
  };

  const handleFileUpload = async () => {
    try {
      const formData = processBeforeAfterImagesData(
        images,
        searchParams?.campaignId ?? '',
        searchParams?.teamId,
        searchParams?.userId,
      );
      const response = await apiCall(() => AttachmentService.postUploadBeforeAfter(formData));

      if (response.status === 200) {
        const newImages = processBeforeAfterImagesResponse(response, images);
        setImages({ ...newImages });
        LoggerService.log('Successfully uploading image', response);
      }
    } catch (error) {
      LoggerService.log('Error uploading image', error);
    }
  };

  const { adminToast } = useAdminToast();
  const [showInbodyPrompt, setShowInbodyPrompt] = useState(false);
  const [inbodyDateList, setInbodyDateList] = useState(false);
  const [inbodyMessage, setInbodyMessage] = useState('');
  const [inbodyDates, setInbodyDates] = useState<InbodyDateOption[]>([]);
  const [assessmentType, setAssessmentType] = useState('');
  const [selectedDate, setSelectedDate] = useState('');

  const handleInbodyData = async (type: string) => {

    setAssessmentType(type);

    if (formData?.phone){
      const [, number] = formData.phone.split(' ');
      let payload = {
        'usertoken' : number
      }
      const response = await apiCall(() => InbodyService.getDateTimes(payload));

      if (response?.data?.status === 200){
        if (response?.data?.data.length > 0){
          const latestRawDates = response.data?.data.sort().slice(-5).reverse(); // most recent 5
          const formatted = latestRawDates.map((date: DynamicObjectInterface) => ({
            date,
            label: formatInbodyDate(date),
          }));
          setInbodyDates(formatted);
          setSelectedDate(formatted[0]?.raw || '');

          setInbodyMessage('These are the latest assessment data available for Inbody ID(' + number + '). Please select one.');
          setInbodyDateList(true);
        }else{
          setInbodyMessage('No assessment data found for this  Inbody ID(' + number + ').');
          setInbodyDateList(false);
        }
      } else if (response?.data?.status === 500) {
        setInbodyMessage('No assessment data found for this  Inbody ID(' + number + ').');
        setInbodyDateList(false);
      } else {
        setInbodyMessage('Unexpected Error. Please try again later');
        setInbodyDateList(false);
      }

    }else{
      setInbodyMessage('Empty Phone Number. Please retry after updating phone information.')
      setInbodyDateList(false);
    }
    setShowInbodyPrompt(true);
  };

  const retrieveInbodyData = async () => {

    if (formData?.phone){
      const [, number] = formData.phone.split(' ');
      let payload = {
        'usertoken' : number,
        'datetimes' : selectedDate
      }
      const response = await apiCall(() => InbodyService.getFullInBodyData(payload));
      if (response.status === 200 && response?.data?.data){

        const year = response.data.data.testDate.substring(0, 4);
        const month = response.data.data.testDate.substring(4, 6);
        const day = response.data.data.testDate.substring(6, 8);

        const weigtedDate = `${year}-${month}-${day}`;

        if (assessmentType === 'pre'){
          formData.weightIn.weightedDate = weigtedDate;
          formData.weightIn.weight= response.data.data.weight;
          formData.weightIn.height= response.data.data.height;
          formData.weightIn.muscleKilogram = response.data.data.smm;
          formData.weightIn.musclePercentage = response.data.data.psmm;
          formData.weightIn.bodyFat = response.data.data.pbf;
        }else if (assessmentType === 'post'){
          formData.weightOut.weightedDate = weigtedDate;
          formData.weightOut.weight= response.data.data.weight;
          formData.weightOut.height= response.data.data.height;
          formData.weightOut.muscleKilogram = response.data.data.smm;
          formData.weightOut.musclePercentage = response.data.data.psmm;
          formData.weightOut.bodyFat = response.data.data.pbf;
        }

        setShowInbodyPrompt(false);
      }
      else{
        adminToast({
          message: 'Unable to get the inbody data!',
          theme: Theme.ERROR,
        });
      }
    }
  }

  const handleInbodyDateOnChange = (selectedOption: DynamicObjectInterface) => {
    setSelectedDate(selectedOption.date);
  };

  const inbodyPrompt = (
    <>
      {showInbodyPrompt && (
        <Modal
          primaryButtonLabel={''}
          headline={`Inbody Data`}
          closeIcon={false}
          containerWidth={ inbodyDateList ? '600px' : '450px'}
          primaryButton={
            inbodyDateList ? (
            <Button
              title={t('admin.participant.confirm')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                retrieveInbodyData();
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
            />
            ) : undefined
          }
          secondaryButton={
            <Button
              title={t('admin.participant.cancel')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                setShowInbodyPrompt(false);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.OUTLINE}
            />
          }
        >
        <div className={styles['modal-inbody']}>   
          {inbodyMessage}
          {inbodyDateList && (
            <div className={styles['inbody-text']}>
              <DropDown
              name={'inbodyDate'}
              dropdownLabel={'Assessment Date'}
              mlpVersion={MLPVersion.ONE}
              value={inbodyDates.find(i => i.date === selectedDate)?.label || 'Please select'}
              data={inbodyDates}
              configData={{
                label: 'label',
                onSelect: (selectedOption: DropDownData) => {
                  handleInbodyDateOnChange(selectedOption);
                },
              }}
            />
          </div> 
          )}
        </div>
      </Modal>
      )}
    </>
  );

  return (
    <Modal
      primaryButtonLabel={''}
      headline={''}
      closeIcon={false}
      containerWidth={'1200px'}
      primaryButton={
        <Button
          title={t('admin.participant.save')}
          type={ButtonTypes.BUTTON}
          onClick={() => {
            onSave(formData);
            handleFileUpload();
          }}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.SOLID}
          data-testid="test-add-button-popup"
        />
      }
      secondaryButton={
        <Button
          title={t('admin.participant.cancel')}
          type={ButtonTypes.BUTTON}
          onClick={onCancel}
          size={ButtonSize.SMALL}
          buttonStyle={ButtonStyles.OUTLINE}
          data-testid="test-cancel-button-popup-delete-modal"
        />
      }
    >
      <div className={styles['modal-upload']}>
        {/* before  */}
        <div className={styles['before-container']}>
          <div className={styles['title']}>Before</div>
          <div className={styles['wrapper']}>
            <button className={styles['inbody-link']} onClick={() => handleInbodyData('pre')}>Retrieve data from Inbody</button>
          </div>
          <div className={styles['upload-container']}>
            <div className={styles['image']}>
              {images.before_front.url ? (
                <div className={styles['image-wrapper']} style={{ position: 'relative' }}>
                  <img src={images.before_front.url} alt="Before Front View" className={styles['uploaded-image']} />
                  <div className={styles['buttons-container']}>
                    <button
                      className={styles['change-photo']}
                      onClick={() => document.getElementById('before_front')?.click()}
                    >
                      <img src={ChangeButton} alt="bg" />
                      <input
                        type="file"
                        id="before_front"
                        accept="image/*"
                        onChange={e => handleFile(e, 'before_front', true)}
                        style={{ display: 'none' }}
                      />
                    </button>
                    <button className={styles['remove-photo']} onClick={() => handleRemoveImage('before', 'front')}>
                      <img src={RemoveButton} alt="bg" />
                    </button>
                  </div>
                </div>
              ) : (
                <ImageEditor onFileUpload={e => handleFileImage(e, 'front', true)} accept="image/*" />
              )}
              <div className={styles['label']}>Front View</div>
            </div>
            <div className={styles['image']}>
              {images.before_side.url ? (
                <div className={styles['image-wrapper']} style={{ position: 'relative' }}>
                  <img src={images.before_side.url} alt="Before Side View" className={styles['uploaded-image']} />
                  <div className={styles['buttons-container']}>
                    <button
                      className={styles['change-photo']}
                      onClick={() => document.getElementById('before_side')?.click()}
                    >
                      <img src={ChangeButton} alt="bg" />
                      <input
                        type="file"
                        id="before_side"
                        accept="image/*"
                        onChange={e => handleFile(e, 'before_side', true)}
                        style={{ display: 'none' }}
                      />
                    </button>
                    <button className={styles['remove-photo']} onClick={() => handleRemoveImage('before', 'side')}>
                      <img src={RemoveButton} alt="bg" />
                    </button>
                  </div>
                </div>
              ) : (
                <ImageEditor onFileUpload={e => handleFileImage(e, 'side', true)} accept="image/*" />
              )}
              <div className={styles['label']}>Side View</div>
            </div>
            <div>
              <div className={styles['before-input-container']}>
                <DatePicker
                  inputContainerHeight="36px"
                  showDateContainer
                  calendarHeading="Pre-Assessment Date"
                  containerWidth="450px"
                  inputLabel={`${t('Pre-Assessment Date')}`}
                  placeholder={`${t('Pre-Assessment Date')}`}
                  value={formData?.weightIn.weightedDate ? new Date(formData?.weightIn.weightedDate) : currentDate}
                  onChange={date => {
                    handleInputChange('weightIn.weightedDate', date?.toISOString());
                  }}
                />
                <Input
                  type={InputTypes.TEXT}
                  name={'beforeMachine'}
                  mlpVersion={MLPVersion.ONE}
                  label={'Machine'}
                  placeholder={'Machine'}
                  value={formData?.weightIn.machine}
                  onChange={e => {
                    handleInputChange('weightIn.machine', e.target.value);
                  }}
                />
                <Input
                  {...commonInputProps}
                  name={'beforeHeight'}
                  label={'Height (cm)'}
                  placeholder={'Height (cm)'}
                  value={formData?.weightIn.height}
                  onChange={e => {
                    const inputValue = e.target.value;
                    if (regex.test(inputValue)) {
                      handleInputChange('weightIn.height', e.target.value);
                    }
                  }}
                />
                <Input
                  {...commonInputProps}
                  name={'beforeWeight'}
                  label={'Weight (kg)'}
                  placeholder={'Weight (kg)'}
                  value={formData?.weightIn.weight}
                  onChange={e => {
                    const inputValue = e.target.value;
                    if (regex.test(inputValue)) {
                      const muscleKg = formData?.weightIn.muscleKilogram || 0;
                      const weightKg: DynamicObjectInterface = inputValue === '' ? '' : inputValue;
                      let musclePercentage = ((muscleKg / weightKg) * 100).toFixed(1);

                      if (musclePercentage === 'Infinity' || musclePercentage === 'NaN') {
                        musclePercentage = '';
                      }

                      handleInputChange('weightIn.weight', weightKg);
                      handleInputChange('weightIn.musclePercentage', musclePercentage);
                    }
                  }}
                />
                <Input
                  {...commonInputProps}
                  name={'beforeMuscleMassKg'}
                  label={'Muscle Mass (kg)'}
                  placeholder={'Muscle Mass (kg)'}
                  value={formData?.weightIn.muscleKilogram}
                  onChange={e => {
                    const inputValue = e.target.value;
                    if (regex.test(inputValue)) {
                      const muscleKg: DynamicObjectInterface = inputValue === '' ? '' : inputValue;
                      const weightKg = formData?.weightIn.weight || 0;
                      let musclePercentage = ((muscleKg / weightKg) * 100).toFixed(1);
                      if (musclePercentage === 'Infinity' || musclePercentage === 'NaN') {
                        musclePercentage = '';
                      }
                      handleInputChange('weightIn.muscleKilogram', muscleKg);
                      handleInputChange('weightIn.musclePercentage', musclePercentage);
                    }
                  }}
                />
                <Input
                  {...commonInputProps}
                  name={'beforeMuscleMassPer'}
                  label={'Muscle Mass (%)'}
                  placeholder={'Muscle Mass (%)'}
                  value={formData?.weightIn.musclePercentage}
                  disabled
                />
                <Input
                  {...commonInputProps}
                  name={'beforeBodyFat'}
                  label={'Body Fat (%)'}
                  placeholder={'Body Fat (%)'}
                  value={formData?.weightIn.bodyFat}
                  onChange={e => {
                    const inputValue = e.target.value;
                    if (regex.test(inputValue)) {
                      handleInputChange('weightIn.bodyFat', inputValue);
                    }
                  }}
                />
              </div>
              <hr className={styles['horizontal-line']} />
              <div className={styles['before-input-container']}>
                <DatePicker
                  inputContainerHeight="36px"
                  showDateContainer
                  calendarHeading="Acknowledged Date"
                  containerWidth="450px"
                  inputLabel={`${t('Acknowledged Date')}`}
                  placeholder={`${t('Acknowledged Date')}`}
                  value={
                    formData?.weightIn.acknowledgeDateTime
                      ? new Date(formData?.weightIn.acknowledgeDateTime)
                      : undefined
                  }
                  onChange={date => {
                    handleInputChange('weightIn.acknowledgeDateTime', date?.toISOString());
                  }}
                />
                {/* <Input
                  {...commonInputProps}
                  name={'beforeAcknowledgedBy'}
                  label={'Acknowledged By'}
                  placeholder={'Acknowledged By'}
                  value={formData?.weightIn.acknowledgeBy ?? ''}
                  // labelInfoPreference={TooltipPreference.TOP_LEFT}
                  // labelInfo="Please key in your LoginID. Numbers only"
                  disabled
                  onChange={e => {
                    handleInputChange('weightIn.acknowledgeBy', Number(e.target.value));
                  }}
                /> */}
                <Input
                  mlpVersion={MLPVersion.ONE}
                  name={'beforeRemark'}
                  label={'Remarks'}
                  placeholder={'Remarks'}
                  value={formData?.weightIn.remarks ?? ''}
                  onChange={e => {
                    handleInputChange('weightIn.remarks', e.target.value);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        {/* after  */}
        <div className={styles['before-container']}>
          <div className={styles['title']}>After</div>
          <div className={styles['wrapper']}>
            <button className={styles['inbody-link']} onClick={() => handleInbodyData('post')}>Retrieve data from Inbody</button>
          </div>
          <div className={styles['upload-container']}>
            <div className={styles['image']}>
              {images.after_front.url ? (
                <div className={styles['image-wrapper']} style={{ position: 'relative' }}>
                  <img src={images.after_front.url} alt="After Front View" className={styles['uploaded-image']} />
                  <div className={styles['buttons-container']}>
                    <button
                      className={styles['change-photo']}
                      onClick={() => document.getElementById('after_front')?.click()}
                    >
                      <img src={ChangeButton} alt="bg" />
                      <input
                        type="file"
                        id="after_front"
                        accept="image/*"
                        onChange={e => handleFile(e, 'after_front', false)}
                        style={{ display: 'none' }}
                      />
                    </button>
                    <button className={styles['remove-photo']} onClick={() => handleRemoveImage('after', 'front')}>
                      <img src={RemoveButton} alt="bg" />
                    </button>
                  </div>
                </div>
              ) : (
                <ImageEditor onFileUpload={e => handleFileImage(e, 'front', false)} accept="image/*" />
              )}
              <div className={styles['label']}>Front View</div>
            </div>
            <div className={styles['image']}>
              {images.after_side.url ? (
                <div className={styles['image-wrapper']} style={{ position: 'relative' }}>
                  <img src={images.after_side.url} alt="After Side View" className={styles['uploaded-image']} />
                  <div className={styles['buttons-container']}>
                    <button
                      className={styles['change-photo']}
                      onClick={() => document.getElementById('after_side')?.click()}
                    >
                      <img src={ChangeButton} alt="bg" />
                      <input
                        type="file"
                        id="after_side"
                        accept="image/*"
                        onChange={e => handleFile(e, 'after_side', false)}
                        style={{ display: 'none' }}
                      />
                    </button>
                    <button className={styles['remove-photo']} onClick={() => handleRemoveImage('after', 'side')}>
                      <img src={RemoveButton} alt="bg" />
                    </button>
                  </div>
                </div>
              ) : (
                <ImageEditor onFileUpload={e => handleFileImage(e, 'side', false)} accept="image/*" />
              )}
              <div className={styles['label']}>Side View</div>
            </div>
            <div>
              <div className={styles['before-input-container']}>
                <DatePicker
                  inputContainerHeight="36px"
                  showDateContainer
                  calendarHeading="Post-Assessment Date"
                  placeholder="Post-Assessment Date"
                  containerWidth="450px"
                  inputLabel={`${t('Post-Assessment Date')}`}
                  value={
                    formData?.weightOut.weightedDate && formData?.weightIn.weightedDate
                      ? new Date(formData?.weightOut.weightedDate)
                      : undefined
                  }
                  onChange={date => {
                    handleInputChange('weightOut.weightedDate', date);
                  }}
                />
                <Input
                  type={InputTypes.TEXT}
                  name={'afterMachine'}
                  mlpVersion={MLPVersion.ONE}
                  placeholder="Machine Name"
                  label={'Machine'}
                  value={formData?.weightOut.machine}
                  onChange={e => {
                    handleInputChange('weightOut.machine', e.target.value);
                  }}
                />

                <Input
                  {...commonInputProps}
                  name={'afterHeight'}
                  label={'Height (cm)'}
                  placeholder="Height (cm)"
                  value={formData?.weightOut.height}
                  onChange={e => {
                    const inputValue = e.target.value;
                    if (regex.test(inputValue)) {
                      handleInputChange('weightOut.height', inputValue);
                    }
                  }}
                />
                <Input
                  {...commonInputProps}
                  name={'afterWeight'}
                  label={'Weight (kg)'}
                  placeholder={'Weight (kg)'}
                  value={formData?.weightOut.weight}
                  onChange={e => {
                    const inputValue = e.target.value;
                    if (regex.test(inputValue)) {
                      const muscleKg = formData?.weightOut.muscleKilogram || 0;
                      const weightKg: DynamicObjectInterface = inputValue === '' ? '' : inputValue;
                      let musclePercentage = ((muscleKg / weightKg) * 100).toFixed(1);
                      if (musclePercentage === 'Infinity' || musclePercentage === 'NaN') {
                        musclePercentage = '';
                      }
                      handleInputChange('weightOut.weight', weightKg);
                      handleInputChange('weightOut.musclePercentage', musclePercentage);
                    }
                  }}
                />

                <Input
                  {...commonInputProps}
                  name={'afterMuscleMassKg'}
                  label={'Muscle Mass (kg)'}
                  placeholder={'Muscle Mass (kg)'}
                  value={formData?.weightOut.muscleKilogram}
                  onChange={e => {
                    const inputValue = e.target.value;
                    if (regex.test(inputValue)) {
                      const muscleKg: DynamicObjectInterface = inputValue === '' ? '' : inputValue;
                      const weightKg = formData?.weightOut.weight || 0;
                      let musclePercentage = ((muscleKg / weightKg) * 100).toFixed(1);
                      if (musclePercentage === 'Infinity' || musclePercentage === 'NaN') {
                        musclePercentage = '';
                      }
                      handleInputChange('weightOut.muscleKilogram', muscleKg);
                      handleInputChange('weightOut.musclePercentage', musclePercentage);
                    }
                  }}
                />
                <Input
                  {...commonInputProps}
                  name={'afterMuscleMassPer'}
                  label={'Muscle Mass (%)'}
                  placeholder={'Muscle Mass (%)'}
                  value={formData?.weightOut.musclePercentage}
                  disabled
                />
                <Input
                  {...commonInputProps}
                  name={'afterBodyFat'}
                  label={'Body Fat (%)'}
                  placeholder={'Body Fat (%)'}
                  value={formData?.weightOut.bodyFat}
                  onChange={e => {
                    const inputValue = e.target.value;
                    if (regex.test(inputValue)) {
                      handleInputChange('weightOut.bodyFat', inputValue);
                    }
                  }}
                />
              </div>
              <hr className={styles['horizontal-line']} />

              <div className={styles['before-input-container']}>
                <DatePicker
                  inputContainerHeight="36px"
                  showDateContainer
                  calendarHeading="Acknowledged Date"
                  containerWidth="450px"
                  inputLabel={`${t('Acknowledged Date')}`}
                  placeholder="Acknowledged Date"
                  value={
                    formData?.weightOut.acknowledgeDateTime
                      ? new Date(formData?.weightOut.acknowledgeDateTime)
                      : undefined
                  }
                  onChange={date => {
                    handleInputChange('weightOut.acknowledgeDateTime', date?.toISOString());
                  }}
                />
                {/* <Input
                  {...commonInputProps}
                  name={'afterAcknowledgedBy'}
                  label={'Acknowledged By'}
                  placeholder="Login ID"
                  // labelInfo="Please key in your LoginID. Numbers only"
                  // labelInfoPreference={TooltipPreference.TOP_LEFT}
                  value={formData?.weightOut.acknowledgeBy ?? ''}
                  disabled
                  onChange={e => {
                    handleInputChange('weightOut.acknowledgeBy', Number(e.target.value));
                  }}
                /> */}
                <Input
                  mlpVersion={MLPVersion.ONE}
                  name={'afterRemark'}
                  label={'Remarks'}
                  placeholder={'Remarks'}
                  value={formData?.weightOut.remarks ?? ''}
                  onChange={e => {
                    handleInputChange('weightOut.remarks', e.target.value);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        {inbodyPrompt}
      </div>
    </Modal>
  );
};
